/**
 * Google Tag Manager dataLayer utility for TempStars
 * Centralized tracking for GTM, Google Ads, GA4, LinkedIn Insight, and other platforms
 * 
 * This utility mirrors the structure of facebookPixel.ts for consistency
 * 
 * GTM Container ID: GTM-5H2VF6B
 * GA4 Measurement ID: G-XWX0H8MV3Y
 * 
 * Features:
 * - Environment detection (dev/staging/production)
 * - Platform detection (web/iOS/Android)
 * - UTM parameter tracking (first-touch attribution)
 * - E-commerce event tracking
 * - User identification
 * - Privacy-safe (no PII without hashing)
 */

// Extend Window interface to include dataLayer
declare global {
  interface Window {
    dataLayer?: any[];
  }
}

/**
 * User types for sign-up completion events
 */
export enum UserType {
  DENTAL_PROFESSIONAL = 'dental_professional',
  DENTAL_OFFICE = 'dental_office'
}

/**
 * Environment detection
 * TempStars environments:
 * - Production: app.tempstars.com
 * - Staging: app.beta.tempstars.com
 * - Development: localhost
 */
const getEnvironment = (): 'production' | 'staging' | 'development' => {
  if (typeof window === 'undefined') return 'development';
  
  const hostname = window.location.hostname;
  
  // Development: localhost
  if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
    return 'development';
  }
  
  // Staging: app.beta.tempstars.com
  if (hostname.includes('beta') || hostname.includes('staging') || hostname.includes('test')) {
    return 'staging';
  }
  
  // Production: app.tempstars.com
  return 'production';
};

const IS_PRODUCTION = getEnvironment() === 'production';
const IS_STAGING = getEnvironment() === 'staging';
const IS_DEVELOPMENT = getEnvironment() === 'development';

/**
 * Platform detection (web vs mobile app)
 * TempStars is a hybrid Cordova app that runs on web, iOS, and Android
 */
const getPlatform = (): 'web' | 'ios' | 'android' => {
  if (typeof window === 'undefined') return 'web';
  
  const cordova = (window as any).cordova;
  
  if (!cordova) return 'web';
  
  if (cordova.platformId === 'ios') return 'ios';
  if (cordova.platformId === 'android') return 'android';
  
  return 'web';
};

/**
 * Check if running in Cordova mobile app
 */
const isMobileApp = (): boolean => {
  return !!(window as any).cordova;
};

/**
 * Get attribution parameters from URL
 * Captures campaign attribution data and click IDs
 */
const CLICK_ID_PARAMS = [
  'fbclid',
  'gclid',
  'dclid',
  'ko_click_id',
  'li_fat_id',
  'msclkid',
  'ttclid',
  'twclid',
  'gbraid',
  'wbraid',
];

const getUTMParameters = (): Record<string, string | null> => {
  if (typeof window === 'undefined') return {};
  
  const params = new URLSearchParams(window.location.search);
  const clickIdParams = CLICK_ID_PARAMS.reduce<Record<string, string | null>>((acc, key) => {
    acc[key] = params.get(key);
    return acc;
  }, {});

  return {
    utm_source: params.get('utm_source'),
    utm_medium: params.get('utm_medium'),
    utm_campaign: params.get('utm_campaign'),
    utm_content: params.get('utm_content'),
    utm_term: params.get('utm_term'),
    ...clickIdParams,
  };
};

/**
 * Get stored attribution parameters from session storage (first-touch)
 * These are captured on first landing and persisted through the session
 */
const getStoredUTMFirstTouch = (): Record<string, string | null> => {
  if (typeof window === 'undefined') return {};
  
  try {
    const stored = sessionStorage.getItem('tempstars_utm_params');
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('[GTM dataLayer] Failed to read stored UTM params:', error);
    return {};
  }
};

const buildFirstTouchAttributionParams = (storedParams: Record<string, string | null>) => {
  const cleanUTMParams = Object.fromEntries(
    Object.entries(storedParams).filter(([_, v]) => v !== null)
  ) as Record<string, string>;

  const clickIdParams = CLICK_ID_PARAMS.reduce<Record<string, string | null>>((acc, key) => {
    acc[key] = cleanUTMParams[key] || null;
    return acc;
  }, {});

  return {
    cleanUTMParams,
    firstTouchParams: {
      utm_source_ft: cleanUTMParams.utm_source || null,
      utm_medium_ft: cleanUTMParams.utm_medium || null,
      utm_campaign_ft: cleanUTMParams.utm_campaign || null,
      utm_content_ft: cleanUTMParams.utm_content || null,
      utm_term_ft: cleanUTMParams.utm_term || null,
      ...clickIdParams,
    },
  };
};

/**
 * Store attribution parameters in session storage
 * Called on landing page to persist attribution through sign-up flow
 * 
 * NOTE: This is already called by facebookPixel.ts (captureUTMParameters)
 * so we just read from the same storage key
 */
export const captureUTMParameters = (): void => {
  if (typeof window === 'undefined') return;
  
  try {
    const params = getUTMParameters();
    // Only store if we have at least one attribution parameter
    if (Object.values(params).some(v => v !== null)) {
      sessionStorage.setItem('tempstars_utm_params', JSON.stringify(params));
      console.log('[GTM dataLayer] Attribution parameters captured:', params);
    }
  } catch (error) {
    console.error('[GTM dataLayer] Failed to store UTM params:', error);
  }
};

/**
 * Rate limiter to prevent accidental infinite loops or excessive pushes
 */
class RateLimiter {
  private lastPush: number = 0;
  private maxPerSecond: number = 10;
  private pushCount: number = 0;

  canPush(): boolean {
    const now = Date.now();
    
    // Reset counter every second
    if (now - this.lastPush >= 1000) {
      this.pushCount = 0;
    }
    
    if (this.pushCount >= this.maxPerSecond) {
      console.warn('[GTM dataLayer] Rate limit hit, skipping push. Max:', this.maxPerSecond, 'per second');
      return false;
    }
    
    this.lastPush = now;
    this.pushCount++;
    return true;
  }
}

const rateLimiter = new RateLimiter();

/**
 * Base dataLayer push function with environment gating and safety checks
 */
const pushToDataLayer = (data: Record<string, any>): void => {
  if (typeof window === 'undefined') {
    console.warn('[GTM dataLayer] Window not available (SSR?)');
    return;
  }
  
  if (!window.dataLayer) {
    console.warn('[GTM dataLayer] dataLayer not initialized. Initializing now...');
    window.dataLayer = [];
  }
  
  // Rate limiting
  if (!rateLimiter.canPush()) {
    return;
  }
  
  // Monitor dataLayer size
  if (window.dataLayer.length > 100) {
    console.warn('[GTM dataLayer] dataLayer has >100 items, potential memory leak. Size:', window.dataLayer.length);
  }
  
  // Add environment and platform metadata to every push
  const enrichedData = {
    ...data,
    _environment: getEnvironment(),
    _platform: getPlatform(),
    _timestamp: new Date().toISOString()
  };
  
  // In development, log instead of pushing (optional - we still push for GTM Preview Mode testing)
  if (IS_DEVELOPMENT) {
    console.log('[GTM dataLayer] DEV MODE - Pushing:', enrichedData);
    window.dataLayer.push(enrichedData);
  } else {
    window.dataLayer.push(enrichedData);
    
    const env = IS_STAGING ? '[STAGING]' : '';
    console.log(`[GTM dataLayer] ${env} Event pushed:`, data.event, enrichedData);
  }
};

/**
 * Track page view
 * Fires on initial page load or route changes
 */
export const trackPageView = (pagePath?: string, pageTitle?: string): void => {
  const platform = getPlatform();
  
  // Warn if on iOS (GTM may be affected by ITP)
  if (platform === 'ios' && IS_PRODUCTION) {
    console.warn('[GTM dataLayer] iOS WebView detected - GTM tracking may be affected by Intelligent Tracking Prevention (ITP)');
  }
  
  pushToDataLayer({
    event: 'page_view',
    page_path: pagePath || window.location.pathname,
    page_title: pageTitle || document.title,
    page_location: window.location.href
  });
};

/**
 * Track sign-up start (equivalent to Facebook's InitiateCheckout)
 * Fires when user is about to submit email/password on Landing page
 */
export const trackSignUpStart = (): void => {
  const platform = getPlatform();
  const utmParams = getStoredUTMFirstTouch();
  const { cleanUTMParams, firstTouchParams } = buildFirstTouchAttributionParams(utmParams);
  
  pushToDataLayer({
    event: 'signup_started',
    engagement_type: 'sign_up',
    ...firstTouchParams
  });
  
  const env = IS_STAGING ? '[STAGING]' : '';
  console.log(`[GTM dataLayer] ${env} signup_started tracked`, {
    platform,
    utmParams: cleanUTMParams
  });
};

/**
 * Track role selection (Dental Office vs Dental Professional)
 * Fires when user selects their role type
 */
export const trackRoleSelected = (roleType: 'dental_office' | 'dental_professional', additionalData?: Record<string, any>): void => {
  const platform = getPlatform();
  const utmParams = getStoredUTMFirstTouch();
  const { firstTouchParams } = buildFirstTouchAttributionParams(utmParams);
  
  pushToDataLayer({
    event: 'role_selected',
    user_type: roleType,
    ...additionalData,
    ...firstTouchParams
  });
  
  const env = IS_STAGING ? '[STAGING]' : '';
  console.log(`[GTM dataLayer] ${env} role_selected tracked`, {
    platform,
    roleType,
    additionalData
  });
};

/**
 * Interface for sign-up complete parameters
 */
interface SignUpCompleteParams {
  userId: string | number;
  userType: UserType;
  userRole?: string;
  country?: 'CA' | 'US';
  province?: string;
  city?: string;
  locationId?: string | number;
  hygienistId?: string | number;
}

/**
 * Track sign-up completion (separate events for Office vs Professional)
 * Fires when user successfully completes their profile
 * 
 * NOTE: This fires AFTER Facebook Pixel's CompleteRegistration event
 */
export const trackSignUpComplete = (params: SignUpCompleteParams): void => {
  const platform = getPlatform();
  const utmParams = getStoredUTMFirstTouch();
  const { firstTouchParams } = buildFirstTouchAttributionParams(utmParams);
  
  // Use separate event names for Office vs Professional
  const eventName = params.userType === UserType.DENTAL_OFFICE 
    ? 'signup_completed_office' 
    : 'signup_completed_professional';
  
  pushToDataLayer({
    event: eventName,
    user_id: String(params.userId),
    user_type: params.userType,
    user_role: params.userRole || 'unknown',
    user_country: params.country || 'unknown',
    user_province: params.province || 'unknown',
    user_city: params.city || 'unknown',
    location_id: params.locationId ? String(params.locationId) : null,
    hygienist_id: params.hygienistId ? String(params.hygienistId) : null,
    ...firstTouchParams
  });
  
  const env = IS_STAGING ? '[STAGING]' : '';
  console.log(`[GTM dataLayer] ${env} ${eventName} tracked`, {
    platform,
    userId: params.userId,
    userType: params.userType
  });
};

/**
 * Interface for e-commerce purchase events
 * Follows GA4 standard e-commerce structure
 */
interface PurchaseParams {
  transactionId: string;
  value: number;
  tax: number;
  currency: 'CAD' | 'USD';
  items: Array<{
    item_id: string;
    item_name: string;
    item_category: string;
    item_category2?: string;
    item_category3?: string;
    price: number;
    quantity: number;
  }>;
  userId?: string | number;
  userType?: string;
  locationId?: string | number;
  jobId?: string | number;
  hireJobId?: string | number;
  [key: string]: any;
}

/**
 * Track e-commerce purchase (GA4 standard)
 * Fires after successful Stripe payment for:
 * - Boosted Posts ($28.25 CAD)
 * - Featured Listings ($28.25 CAD)
 * - Job Board Tokens ($18 CAD per token)
 */
export const trackPurchase = (params: PurchaseParams): void => {
  const platform = getPlatform();
  const utmParams = getStoredUTMFirstTouch();
  const { firstTouchParams } = buildFirstTouchAttributionParams(utmParams);
  
  pushToDataLayer({
    event: 'purchase',
    ecommerce: {
      transaction_id: params.transactionId,
      affiliation: 'TempStars',
      value: params.value,
      tax: params.tax,
      shipping: 0,
      currency: params.currency,
      coupon: '',
      items: params.items
    },
    user_id: params.userId ? String(params.userId) : null,
    user_type: params.userType || null,
    location_id: params.locationId ? String(params.locationId) : null,
    job_id: params.jobId ? String(params.jobId) : null,
    hire_job_id: params.hireJobId ? String(params.hireJobId) : null,
    ...firstTouchParams
  });
  
  const env = IS_STAGING ? '[STAGING]' : '';
  console.log(`[GTM dataLayer] ${env} purchase tracked`, {
    platform,
    transactionId: params.transactionId,
    value: params.value,
    currency: params.currency,
    itemCount: params.items.length
  });
};

/**
 * Track custom event (for additional insights)
 * Use for events not covered by standard GTM events
 */
export const trackEvent = (
  eventName: string,
  eventParams?: Record<string, any>
): void => {
  pushToDataLayer({
    event: eventName,
    ...eventParams
  });
};

/**
 * Track user identification (for authenticated sessions)
 * Fires after login or on page load for authenticated users
 */
export const trackUserIdentified = (params: SignUpCompleteParams): void => {
  pushToDataLayer({
    event: 'user_identified',
    user_id: String(params.userId),
    user_type: params.userType,
    user_role: params.userRole || 'unknown',
    user_country: params.country || 'unknown',
    user_province: params.province || 'unknown',
    user_city: params.city || 'unknown',
    location_id: params.locationId ? String(params.locationId) : null,
    hygienist_id: params.hygienistId ? String(params.hygienistId) : null
  });
  
  const env = IS_STAGING ? '[STAGING]' : '';
  console.log(`[GTM dataLayer] ${env} user_identified tracked`, {
    userId: params.userId,
    userType: params.userType
  });
};

/**
 * Get current environment (for debugging)
 */
export const getGTMEnvironment = (): string => {
  return getEnvironment();
};

/**
 * Get current platform
 */
export const getGTMPlatform = (): string => {
  return getPlatform();
};

/**
 * Check if GTM should fire in current environment
 */
export const shouldFireGTM = (): boolean => {
  // Fire in all environments to allow GTM Preview Mode testing on localhost
  return true;
};

/**
 * Check if running in mobile app (iOS or Android)
 */
export const isGTMInMobileApp = (): boolean => {
  return isMobileApp();
};

/**
 * Get dataLayer stats for monitoring
 */
export const getDataLayerStats = (): { size: number; memory: number } => {
  if (typeof window === 'undefined' || !window.dataLayer) {
    return { size: 0, memory: 0 };
  }
  
  return {
    size: window.dataLayer.length,
    memory: JSON.stringify(window.dataLayer).length
  };
};

/**
 * Clear dataLayer (use with caution - mainly for testing)
 */
export const clearDataLayer = (): void => {
  if (typeof window !== 'undefined' && window.dataLayer) {
    console.warn('[GTM dataLayer] Clearing dataLayer');
    window.dataLayer = [];
  }
};

